import React from 'react';
import type { Attribute } from '@tiptap/core';
import { Placeholder } from '@tiptap/extensions';
import { useEditor, EditorOptions } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { OrderedList as TiptapOrderedList } from '@tiptap/extension-ordered-list';
import { Box } from 'pounceV2';
import { useTheme } from '@mui/material';

export const RichTextEditorStylesV3 = ({ children }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        // ProseMirro (Tiptap is built on top of that) applies some default editor styles via CSS classes, so there's not an easy way to style the editor apart from
        // the `sx` prop
        // Base markdown styles
        '.ProseMirror': {
          wordBreak: 'break-word',
          whiteSpace: 'pre-wrap',
          position: 'relative',
        },
        '.ProseMirror [contenteditable="false"]': {
          whiteSpace: 'normal',
        },
        '.ProseMirror [contenteditable="false"] [contenteditable="true"]': {
          whiteSpace: 'pre-wrap',
        },
        '.ProseMirror > * + *': {
          whiteSpace: 'pre-wrap',
          marginTop: '0.75em',
        },
        '.ProseMirror ul, .ProseMirror ol': {
          padding: '0 1rem',
          // `pounce` resets list styles by default, so we need to add them back in here
          // @ts-ignore
          listStyle: 'unset',
          // 'list-style': 'unset',
        },
        '.ProseMirror ol': {
          // @ts-ignore
          listStyleType: 'decimal',
          // 'list-style-type': 'decimal',
        },
        '.ProseMirror ul': {
          // @ts-ignore
          // 'list-style-type': 'disc',
          listStyleType: 'disc',
        },
        '.ProseMirror h1,.ProseMirror h2,.ProseMirror h3,.ProseMirror h4,.ProseMirror h5,.ProseMirror h6':
          {
            lineHeight: 'snug',
          },
        '.ProseMirror hr': {
          margin: '1rem 0',
        },
        '.ProseMirror blockquote': {
          paddingLeft: '1rem',
          borderLeft: '3px solid',
          borderLeftColor: theme.palette.tokens.stroke.neutral[5],
        },
        '.ProseMirror a': {
          color: theme.palette.tokens.content.text.accent[14].base,
          cursor: 'pointer',
          textDecoration: 'underline',
        },
        '.ProseMirror pre': {
          fontFamily: theme.palette.typographyTokens.fontFamilies.mono,
          padding: '0.5rem',
          border: '1px solid',
          borderColor: theme.palette.tokens.stroke.neutral[4],
          backgroundColor: theme.palette.componentTokens.inputs.colors.normal.background.resting,
          borderRadius: '6px',
          whiteSpace: 'pre-wrap',
        },

        // Styles for placeholder text
        '.ProseMirror p.is-editor-empty:first-of-type::before': {
          // @ts-ignore
          color: theme.palette.componentTokens.inputs.colors.normal.text.inputText.resting,
          // @ts-ignore
          content: 'attr(data-placeholder)',
          // @ts-ignore
          position: 'absolute',
          // @ts-ignore
          height: '0',
          pointerEvents: 'none',
        },
      }}
    >
      {children}
    </Box>
  );
};

const CustomOrderedList = TiptapOrderedList.extend({
  addAttributes() {
    // Parent return type is `{} | Attributes` in typings; narrow so we can read `start`.
    const parentAttributes = this.parent?.() as Record<string, Attribute> | undefined;

    // We return an object that only includes the start attribute since we don't
    // want to include the type attribute. The type attribute is new in tiptap v3,
    // and we don't want to include it since our comment parsing code isn't expecting it.
    return {
      start: parentAttributes?.start,
    };
  },
});

const CustomStarterKit = StarterKit.configure({
  gapcursor: false,
  dropcursor: false,
  link: {
    HTMLAttributes: {
      target: undefined,
      class: undefined,
    },
  },
  // Disable the default orderedList so we can override it with our own in the
  // extensions array below.
  orderedList: false,
});

const useRichTextEditorV3 = ({ editable, ...rest }: Partial<EditorOptions>) => {
  const editor = useEditor({
    extensions: [
      CustomStarterKit,
      CustomOrderedList,
      Placeholder.configure({
        placeholder: 'Write your message here …',
      }),
    ],
    content: '',
    editable,
    injectCSS: false,
    // This ensures the editor is rerendered when content changes
    shouldRerenderOnTransaction: true,
    ...rest,
  });

  return editor;
};

export default useRichTextEditorV3;
